import {
  $,
  useSignal,
  component$,
  type JSXChildren,
  useVisibleTask$,
  type PropFunction,
} from "@builder.io/qwik";

export type SimpleCarouselProps<T> = {
  items: T[];
  itemWidth?: number;
  selectedIndex?: number;
  onSelectedIndexChange$?: PropFunction<(index: number) => void>;
  renderItem: (item: T, index: number, isActive: boolean) => JSXChildren;
};

export const SimpleCarousel = component$(
  <T,>({
    items,
    renderItem,
    itemWidth = 280,
    selectedIndex = 0,
    onSelectedIndexChange$,
  }: SimpleCarouselProps<T>) => {
    const containerRef = useSignal<HTMLDivElement>();
    const currentIndex = useSignal(selectedIndex);
    const visibleIndex = useSignal(selectedIndex);

    const scrollToItem = $((index: number) => {
      if (!containerRef.value) return;

      // Ensure index is within bounds
      const boundedIndex = Math.max(0, Math.min(index, items.length - 1));
      visibleIndex.value = boundedIndex;

      const itemElement = containerRef.value.children[
        boundedIndex
      ] as HTMLElement;
      if (!itemElement) return;

      itemElement.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    });

    const scrollPrev = $(() => {
      scrollToItem(visibleIndex.value - 1);
    });

    const scrollNext = $(() => {
      scrollToItem(visibleIndex.value + 1);
    });

    const selectItem = $((index: number) => {
      const boundedIndex = Math.max(0, Math.min(index, items.length - 1));
      currentIndex.value = boundedIndex;
      onSelectedIndexChange$?.(boundedIndex);
    });

    // Needs to run on frontend
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      scrollToItem(visibleIndex.value);
    });

    return (
      <div class="relative w-full">
        {/* Left Button */}
        <button
          onClick$={scrollPrev}
          disabled={visibleIndex.value <= 0}
          class="hover:bg-brand/10 absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-all duration-300 disabled:opacity-40 disabled:hover:bg-white"
          aria-label="Previous item"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-brand h-5 w-5"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div
          ref={containerRef}
          class="no-scrollbar flex scroll-px-4 gap-4 overflow-x-auto scroll-smooth px-4 pb-4 [scrollbar-width:none] [&>*]:scroll-mx-4"
        >
          {items.map((item, index) => (
            <div
              key={index}
              style={{ width: `${itemWidth}px` }}
              class={`flex-none scale-100 opacity-100 transition-all duration-500 ease-[cubic-bezier(0.33,1,0.68,1)]`}
              onClick$={() => selectItem(index)}
            >
              {renderItem(item, index, currentIndex.value === index)}
            </div>
          ))}
        </div>

        {/* Right Button */}
        <button
          onClick$={scrollNext}
          disabled={visibleIndex.value >= items.length - 1}
          class="hover:bg-brand/10 absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md transition-all duration-300 disabled:opacity-40 disabled:hover:bg-white"
          aria-label="Next item"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-brand h-5 w-5"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    );
  },
);
